import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Toggle, Icon, Radio, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewStore, updateStore, deleteStore } from '../../global/Firebase/firebase'
import HeaderIconButton from '../../components/HeaderIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { getAccessToken, getProfiles, getCashboxes, getSalePoints, getSaleSections } from '../../global/wipon'
import { useTranslation } from 'react-i18next'
import { WORKING_HOURS } from '../Shift/ShiftScreen'
import timezones from '../../utils/timezones'
import { HintView } from '../../components/HintView'
import { useAtomValue } from 'jotai'
import { constantsAtom, productsDictAtom, storesDictAtom, usersListAtom } from '../../global/AtomsProvider'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { showsOnTopFor, withShowOnTopFor } from '../../utils/showOnTop'
import { buildWiponCredentials, loadWiponOptions, toWiponSelectorItems, wiponRefreshMessageKey, WIPON_REFRESH_OK } from '../../utils/wiponSettings'

const FISCAL_OPERATORS = [
  {id: 'none'},
  {id: 'light-kassa', path: 'LKCredentials'},
  {id: 'wipon-kassa', path: 'WiponCredentials'}
]

export default function StoreDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const [context, setContext] = useContext(UserContext)
  const { t } = useTranslation()
  const { detail } = route.params
  const productsDict = useAtomValue(productsDictAtom)
  const usersList = useAtomValue(usersListAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const constants = useAtomValue(constantsAtom)
  const [name, setName] = useState(detail ? detail.name : '')
  const [address, setAddress] = useState(detail && detail.address ? detail.address : '')
  const [autoCloseShiftTime, setAutoCloseShiftTime] = useState(detail && detail.autoCloseShiftTime ? detail.autoCloseShiftTime : WORKING_HOURS[0])
  const [timezone, setTimezone] = useState(detail && detail.timezone ? detail.timezone : null)
  const [copySettingsFrom, setCopySettingsFrom] = useState(Object.values(storesDict)?.length > 0 ? Object.values(storesDict)[0] : null) 
  const [fiscalOperator, setFiscalOperator] = useState(detail?.fiscalOperator || FISCAL_OPERATORS[0])
  const [LKCashId, setLKCashId] = useState(detail?.LKCredentials?.cashId?.toString() || '')
  const [LKPin, setLKPin] = useState(detail?.LKCredentials?.pin || '')
  const [LKUsername, setLKUsername] = useState(detail?.LKCredentials?.username || '')
  const [LKPassword, setLKPassword] = useState(detail?.LKCredentials?.password || '')
  const [wiponUsername, setWiponUsername] = useState(detail?.wiponCredentials?.username || '')
  const [wiponPassword, setWiponPassword] = useState(detail?.wiponCredentials?.password || '')
  const [wiponAccessToken, setWiponAccessToken] = useState(detail?.wiponCredentials?.accessToken || '')
  const [wiponRefreshError, setWiponRefreshError] = useState({selector: '', message: ''})
  const [wiponProfiles, setWiponProfiles] = useState(detail?.wiponCredentials?.allProfiles || [])
  const [wiponProfile, setWiponProfile] = useState(detail?.wiponCredentials?.profile || '')
  const [wiponCashboxes, setWiponCashboxes] = useState(detail?.wiponCredentials?.allCashboxes || [])
  const [wiponCashbox, setWiponCashbox] = useState(detail?.wiponCredentials?.cashbox || '')
  const [wiponVatType, setWiponVatType] = useState(detail?.wiponCredentials?.vatType || 0)
  const [wiponSaleSection, setWiponSaleSection] = useState(detail?.wiponCredentials?.saleSection || 0)
  const [wiponSaleSections, setWiponSaleSections] = useState(detail?.wiponCredentials?.allSaleSections || [])
  const [wiponSalePoint, setWiponSalePoint] = useState(detail?.wiponCredentials?.salePoint || 0)
  const [wiponSalePoints, setWiponSalePoints] = useState(detail?.wiponCredentials?.allSalePoints || [])
  // ATL-1098 — the toggle shows and edits the ADMIN half only, which is what this screen has always
  // displayed and what HomeScreen reads. `detail.showOnTop` is EITHER a boolean OR
  // { admin, cashier } (2,627 of the fleet's 5,814 stores hold the map).
  //
  // Reading the half is safe. What was NOT safe is what `save()` used to do with it: put this bare
  // boolean straight back into `info.showOnTop`, which `updateStore` writes to Firestore AND
  // Supabase — so opening this screen and pressing Save on a map-holding store DELETED the
  // `cashier` half permanently. The original value is therefore kept below and the write is
  // rebuilt onto it (`withShowOnTopFor`), never replaced by it.
  //
  // A store with no `showOnTop` at all defaults to shown, matching how new stores are seeded.
  const [showOnTop, setShowOnTop] = useState(detail?.showOnTop != null ? showsOnTopFor(detail.showOnTop, 'admin') : true)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const localizedFiscalOperators = FISCAL_OPERATORS.map(item => ({...item, text: t('stores.ofd' + item.id), name: t('stores.ofd' + item.id)}))

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            if (Object.values(storesDict).length == 1) {
              alert(t('stores.singleStoreDeletionError'))
              return
            }
            // check if there are no active users left in the store
            const activeUsers = usersList.filter(user => user.store.id == detail.id && user.active)
            if (activeUsers.length > 0) {
              alert(t('stores.activeUsersPresentInStoreError') + activeUsers.map(user => user.name).join(', '))
              return
            }
            // ATL-911: pass the org's products so the store's per-product keys
            // (salePrices/activeInStore/storeStock) are pruned with it, and AWAIT before
            // navigating — the old floating call raced a screen teardown, and `goBack()` fired
            // while the delete was still in flight.
            void (async () => {
              await deleteStore(detail.ref, Object.values(productsDict))
              navigation.goBack()
            })()

          }}
          iconName='trash-outline'
        />
      ),
      title: (detail?.name || '')
    })
  }, [])

  // Log store detail for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[STORE_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        address: detail.address,
        autoCloseShiftTime: detail.autoCloseShiftTime,
        timezone: detail.timezone,
        showOnTop: detail.showOnTop,
        fiscalOperator: detail.fiscalOperator,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [detail])

  // Named rather than positional, so inserting an argument can never silently strip the profile
  // precondition off a selector and put it back to burning a login and then blaming the password.
  const refreshWiponOptions = async ({selector, getOptions, setOptions, optionText, selectionCredentials = {}, requiresProfile = false}) => {
    const result = await loadWiponOptions({
      accessToken: wiponAccessToken,
      username: wiponUsername,
      password: wiponPassword,
      selectionCredentials,
      requiresProfile,
      getAccessToken,
      getOptions,
    })

    // Keep whatever session proved usable, even when the answer was empty. Before ATL-1356 every
    // subsequent selector retried the stale token, and Save omitted the replacement token entirely,
    // so the store ended up with no `accessToken` at all and each refresh depended on a re-login.
    if (result.accessToken) setWiponAccessToken(result.accessToken)

    if (result.status === WIPON_REFRESH_OK) {
      setOptions(toWiponSelectorItems(result.options, optionText))
      setWiponRefreshError({selector: '', message: ''})
      return
    }

    // Anything short of a real list leaves the cached options and the stored selection untouched
    // and says why, beside the button that was pressed. Replacing them with nothing is how a branch
    // loses the ability to switch cashbox at all, which is the defect ATL-1356 was raised for.
    setWiponRefreshError({selector, message: t(wiponRefreshMessageKey(result.status))})
  }

  // The outcome belongs next to the selector it came from. Reporting it in the shared save-error
  // slot at the foot of the form put it below the VAT radios — off-screen on a phone, which is
  // indistinguishable from the silent failure this ticket exists to remove.
  const renderWiponRefreshError = (selector) => (
    wiponRefreshError.selector === selector && wiponRefreshError.message !== '' ? (
      <Text status='danger' category='c1' style={styles.label}>{wiponRefreshError.message}</Text>
    ) : null
  )

  const getWiponProfiles = async () => {
    await refreshWiponOptions({
      selector: 'profile',
      getOptions: getProfiles,
      setOptions: setWiponProfiles,
      optionText: value => value.company?.name,
    })
  }

  const getWiponCashboxes = async () => {
    await refreshWiponOptions({
      selector: 'cashbox',
      getOptions: getCashboxes,
      setOptions: setWiponCashboxes,
      optionText: value => value.name,
      selectionCredentials: {profile: wiponProfile},
      requiresProfile: true,
    })
  }

  const getWiponSalePoints = async () => {
    await refreshWiponOptions({
      selector: 'salePoint',
      getOptions: getSalePoints,
      setOptions: setWiponSalePoints,
      optionText: value => value.name,
      selectionCredentials: {profile: wiponProfile},
      requiresProfile: true,
    })
  }

  const getWiponSaleSections = async () => {
    await refreshWiponOptions({
      selector: 'saleSection',
      getOptions: getSaleSections,
      setOptions: setWiponSaleSections,
      optionText: value => value.name,
      selectionCredentials: {profile: wiponProfile},
      requiresProfile: true,
    })
  }

  async function save() {
    setIsLoading(true)
    if (name !== '') {
      setSaveError('')
      let info = {
        name,
        address,
        // ATL-1098: shape-preserving. Writes the admin half onto whatever shape the store already
        // has — a map stays a map with its `cashier` half untouched, a boolean stays a boolean.
        // NEVER write the bare `showOnTop` state here: it is one half of a two-half value, and the
        // dual-write makes the loss permanent in both stores at once.
        showOnTop: withShowOnTopFor(detail?.showOnTop, 'admin', showOnTop),
        timezone, 
        autoCloseShiftTime,
        fiscalOperator,
        organizationId: context.user.organizationId,
      }
      if (LKUsername !== '') {
        let cashIdInt = parseInt(LKCashId)
        let LKCredentials = {
          cashId: cashIdInt,
          pin: LKPin,
          username: LKUsername,
          password: LKPassword
        }
        info.LKCredentials = LKCredentials
      }
      if (wiponUsername !== '') {
        let wiponCredentials = buildWiponCredentials({
          username: wiponUsername,
          password: wiponPassword,
          accessToken: wiponAccessToken,
          profile: wiponProfile,
          cashbox: wiponCashbox,
          allProfiles: wiponProfiles,
          allCashboxes: wiponCashboxes,
          salePoint: wiponSalePoint,
          allSalePoints: wiponSalePoints,
          saleSection: wiponSaleSection,
          allSaleSections: wiponSaleSections,
          vatType: wiponVatType
        })
        info.wiponCredentials = wiponCredentials
      }
      try {
        if (detail == null) {
          await saveNewStore(context.db, info, Object.values(productsDict), constants, copySettingsFrom)
        } else {
          await updateStore(detail.ref, info)
        }
        setSuccessMessageVisible(true)
        setIsLoading(false)
      } catch (error) {
        if (error.message?.startsWith('MISSING_WALLET_CONFIG:')) {
          const missingMethods = error.message.replace('MISSING_WALLET_CONFIG:', '').split(',')
          setSaveError(t('stores.missingWalletConfigError', { methods: missingMethods.join(', ') }))
        } else if (error.message === 'CONSTANTS_NOT_LOADED') {
          setSaveError(t('stores.constantsNotLoadedError'))
        } else {
          Sentry.captureException(error)
          console.log(error)
          setSaveError(t('error'))
        }
        setIsLoading(false)
      }
    } else {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
    }
  }

  

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
    
        <Text category='label' style={styles.label}>
          {t('stores.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />

        <Text category='label' style={styles.label}>
          {t('stores.address')}
        </Text>
        <StyledTextInput 
          value={address}
          onChangeText={text => setAddress(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />

        <Text category='label' style={styles.label}>
          {t('stores.autoCloseShiftTime')}
        </Text>
        <HintView
          text={t('stores.autoCloseShiftTimeHint')}
          iconName='info-outline'
          style={{marginBottom: 10}}
        />
        <PlatformDependantSelect
          data={WORKING_HOURS.filter((value, index) => value.value < 6)} 
          selectedOption={autoCloseShiftTime}
          onSelect={(itemValue, itemIndex) => {
            setAutoCloseShiftTime(itemValue)
          }}
          withSearch={true}
        />

        <Text category='label' style={styles.label}>
          {t('stores.timezone')}
        </Text>
        <PlatformDependantSelect
          data={timezones} 
          selectedOption={timezone}
          onSelect={(itemValue, itemIndex) => {
            setTimezone(itemValue)
          }}
          withSearch={true}
        />

        <Text category='label' style={styles.label}>
          {t('stores.showStatsOnTop')}
        </Text>
        <Toggle 
          checked={showOnTop}
          onChange={setShowOnTop}
          style={styles.toggleField}
        >
          {t('stores.show')}
        </Toggle>

        { detail == null && (
          <>
            <Text category='label' style={styles.label}>
            {t('stores.copySettingsFrom')}
            </Text>
            <PlatformDependantSelect
              data={Object.values(storesDict)}
              selectedOption={copySettingsFrom}
              onSelect={setCopySettingsFrom}
            />
          </>
        )}

        <Text category='label' style={styles.label}>
          {t('stores.ofd')}
        </Text>
        <PlatformDependantSelect
          data={localizedFiscalOperators}
          selectedOption={fiscalOperator}
          onSelect={setFiscalOperator}
        />

        { fiscalOperator?.id === 'light-kassa' && (
          <View>
            <Text category='h3' style={styles.label}>{t('stores.lightKassaInfo')}</Text>
            <Text category='label' style={styles.label}>
              {t('stores.cashboxId')}
            </Text>
            <StyledTextInput 
              value={LKCashId}
              onChangeText={text => setLKCashId(text)}
              returnKeyType='done'
              keyboardType='number-pad'
              placeholder='00000'
            />
            <Text category='label' style={styles.label}>
              {t('stores.cashboxPin')}
            </Text>
            <StyledTextInput 
              value={LKPin}
              onChangeText={text => setLKPin(text)}
              returnKeyType='done'
              keyboardType='number-pad'
              placeholder='0000'
            />
            <Text category='label' style={styles.label}>
              {t('stores.login')}
            </Text>
            <StyledTextInput 
              value={LKUsername}
              onChangeText={text => setLKUsername(text)}
              returnKeyType='done'
              keyboardType='number-pad'
              placeholder='7013330022'
            />
            <Text category='label' style={styles.label}>
              {t('stores.password')}
            </Text>
            <StyledTextInput 
              value={LKPassword}
              onChangeText={text => setLKPassword(text)}
              returnKeyType='done'
            />
          </View>
        )}

        { fiscalOperator?.id === 'wipon-kassa' && (
          <View>
            <Text category='h3' style={styles.label}>{t('stores.wiponInfo')}</Text>
            <Text category='label' style={styles.label}>
              {t('stores.login')}
            </Text>
            <StyledTextInput 
              value={wiponUsername}
              onChangeText={text => setWiponUsername(text)}
              returnKeyType='done'
              keyboardType='number-pad'
              placeholder='77013330022'
            />
            <Text category='label' style={styles.label}>
              {t('stores.password')}
            </Text>
            <StyledTextInput 
              value={wiponPassword}
              onChangeText={text => setWiponPassword(text)}
              returnKeyType='done'
            />
            <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
              <Text category='label'>
                {t('stores.profile')}
              </Text>
              <TouchableOpacity onPress={() => {void getWiponProfiles()}} style={{marginLeft: 5, padding: 3}}>
                <Icon name='refresh-outline' height={15} width={15} fill={theme['color-primary-500']} />
              </TouchableOpacity>
            </View>
            <PlatformDependantSelect
              data={wiponProfiles}
              selectedOption={wiponProfile}
              onSelect={setWiponProfile}
            />
            {renderWiponRefreshError('profile')}
            <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
              <Text category='label'>
                {t('stores.cashbox')}
              </Text>
              <TouchableOpacity onPress={() => {void getWiponCashboxes()}} style={{marginLeft: 5, padding: 3}}>
                <Icon name='refresh-outline' height={15} width={15} fill={theme['color-primary-500']} />
              </TouchableOpacity>
            </View>
            <PlatformDependantSelect
              data={wiponCashboxes}
              selectedOption={wiponCashbox}
              onSelect={setWiponCashbox}
            />
            {renderWiponRefreshError('cashbox')}
            <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
              <Text category='label'>
                {t('stores.salePoint')}
              </Text>
              <TouchableOpacity onPress={() => {void getWiponSalePoints()}} style={{marginLeft: 5, padding: 3}}>
                <Icon name='refresh-outline' height={15} width={15} fill={theme['color-primary-500']} />
              </TouchableOpacity>
            </View>
            <PlatformDependantSelect
              data={wiponSalePoints}
              selectedOption={wiponSalePoint}
              onSelect={setWiponSalePoint}
            />
            {renderWiponRefreshError('salePoint')}
            <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
              <Text category='label'>
                {t('stores.saleSection')}
              </Text>
              <TouchableOpacity onPress={() => {void getWiponSaleSections()}} style={{marginLeft: 5, padding: 3}}>
                <Icon name='refresh-outline' height={15} width={15} fill={theme['color-primary-500']} />
              </TouchableOpacity>
            </View>
            <PlatformDependantSelect
              data={wiponSaleSections}
              selectedOption={wiponSaleSection}
              onSelect={setWiponSaleSection}
            />
            {renderWiponRefreshError('saleSection')}
            <Text category='label' style={styles.label}>{t('stores.vatType')}</Text>
            <Radio style={styles.radio} checked={wiponVatType === 0} onChange={() => {setWiponVatType(0)}}>
              {t('stores.withoutVat')}
            </Radio>
            <Radio style={styles.radio} checked={wiponVatType === 1} onChange={() => {setWiponVatType(1)}}>
              {t('stores.vat12')}
            </Radio>
          </View>
        )}
        
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    marginBottom: 15,
    alignSelf: 'flex-start'
  },
  radio: {
    marginBottom: 5
  },
});
